import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import Popover from '@mui/material/Popover'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import ChecklistIcon from '@mui/icons-material/Checklist'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'

import { ReadinessChecklistState } from '../model/useReadinessChecklist'

/**
 * Topbar preparation-checklist control.
 * - Boolean-only template: a "3/5" button with an inline checkbox popover (each tick auto-saves).
 * - Any non-boolean field: a button that opens the dedicated form-submission page (renders every
 *   field type); submitting it marks the patient "prepared" for the waiting doctors.
 */
export function ReadinessChecklistButton({ checklist }: { checklist: ReadinessChecklistState }) {
  const { t } = useTranslation()
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)

  if (!checklist.enabled) return null

  if (checklist.hasNonBooleanFields) {
    const submitted = checklist.status === 'submitted'
    const label = submitted
      ? t('page.liveConsultation.topbar.checklist.prepared', { defaultValue: 'Preparation submitted' })
      : checklist.status === 'draft'
      ? t('page.liveConsultation.topbar.checklist.continue', { defaultValue: 'Continue preparation' })
      : t('page.liveConsultation.topbar.checklist.fill', { defaultValue: 'Fill preparation form' })
    return (
      <Tooltip title={t('page.liveConsultation.topbar.checklist.formTooltip', { defaultValue: 'Open the preparation form for this patient' })}>
        <Button
          color={submitted ? 'success' : 'warning'}
          onClick={() => checklist.openPreparation()}
          size="small"
          variant="outlined"
          startIcon={submitted ? <CheckCircleIcon /> : <ChecklistIcon />}
          sx={{ borderRadius: '15px', fontSize: '12px', backgroundColor: 'white' }}
          data-testid="readiness-checklist-form-button"
        >
          {label}
        </Button>
      </Tooltip>
    )
  }

  return (
    <>
      <Tooltip title={t('page.liveConsultation.topbar.checklist.tooltip')}>
        <Button
          color={checklist.completed === checklist.total ? 'success' : 'warning'}
          onClick={(event) => setAnchorEl(event.currentTarget)}
          size="small"
          variant="outlined"
          startIcon={<ChecklistIcon />}
          sx={{ borderRadius: '15px', fontSize: '12px', backgroundColor: 'white' }}
          data-testid="readiness-checklist-button"
        >
          {`${t('page.liveConsultation.topbar.checklist.label')} ${checklist.completed}/${checklist.total}`}
        </Button>
      </Tooltip>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Stack spacing={0.5} sx={{ p: 2, minWidth: 260 }} data-testid="readiness-checklist-popover">
          <Typography variant="subtitle2">{checklist.templateTitle}</Typography>
          {checklist.items.map((item) => (
            <FormControlLabel
              key={item.fieldId}
              control={
                <Checkbox
                  checked={item.done}
                  disabled={checklist.isSaving}
                  onChange={(event) => checklist.toggleItem(item.fieldId, event.target.checked)}
                  size="small"
                />
              }
              label={item.label}
            />
          ))}
        </Stack>
      </Popover>
    </>
  )
}
